'use client';

import React, {
  useState,
  ReactElement,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faAngleDown,
  faAngleUp,
  faCaretDown,
  faEllipsisV,
} from '@fortawesome/pro-solid-svg-icons';
import { useClickOutside } from 'src/hooks/common/useClickOutside';
import FilterPill from '../FilterPill';
import { Checkbox, CircularProgress } from 'src/components/PulseUI';
import { exists } from 'src/utils/exists/exists';

interface SegmentedMultiSelectMenuProps {
  filterHeader: string;
  optionType: string;
  optionData: { [key: string]: { label: string; id: string }[] };
  startDecorator: ReactElement;
  selectedOptions: { [key: string]: string[] };
  setSelectedOptions: (
    optionType: string,
    options: { [key: string]: string[] }
  ) => void;
  onMenuScroll: (isActivity: boolean) => void;
  isLoading: boolean;
  expandedSections: { [key: string]: boolean };
  setExpandedSections: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
  testId?: string;
}

const SegmentedMultiSelectMenu = ({
  filterHeader,
  optionType,
  optionData,
  startDecorator,
  selectedOptions,
  setSelectedOptions,
  onMenuScroll,
  isLoading,
  expandedSections,
  setExpandedSections,
  testId = 'segmented-multi-select',
}: SegmentedMultiSelectMenuProps) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const moreOptionsMenuRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isMoreOptionsOpen, setIsMoreOptionsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [allSelected, setAllSelected] = useState(false);

  const toggleSection = (header: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [header]: !prev[header],
    }));
  };

  const selectedOptionsCombinedLength = Object.values(selectedOptions).reduce(
    (acc, optionsArray) => acc + optionsArray.length,
    0
  );

  useEffect(() => {
    if (
      Object.keys(selectedOptions).length === Object.keys(optionData).length
    ) {
      setAllSelected(true);
    } else {
      setAllSelected(false);
    }
  }, [optionData?.length, selectedOptions?.length]);

  const updateMenuPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
  };

  const handleOpenMenu = () => {
    if (!isOpen) {
      updateMenuPosition();
    }
    setIsOpen(!isOpen);
    setIsMoreOptionsOpen(false);
  };

  const handleMoreOptionsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMoreOptionsOpen(!isMoreOptionsOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsMoreOptionsOpen(false);
  };

  const handleCloseMoreOptions = () => {
    setIsMoreOptionsOpen(false);
  };

  const selectAll = (e: React.MouseEvent) => {
    e.stopPropagation();

    const allFormattedOptions = Object.fromEntries(
      Object.entries(optionData).map(([key, valueArray]) => [
        key,
        valueArray.map((item) => item.id),
      ])
    );

    setAllSelected(true);
    setSelectedOptions(optionType, allFormattedOptions);
    setIsMoreOptionsOpen(false);
  };

  const deselectAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAllSelected(false);
    setSelectedOptions(optionType, {});
    setIsMoreOptionsOpen(false);
  };

  const handleCheckboxChange = (
    header: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.stopPropagation();
    const value = e.target?.value;

    const formattedOptions = () => {
      const newStatuses = { ...selectedOptions };
      if (newStatuses[header]?.includes(value)) {
        newStatuses[header] = newStatuses[header].filter(
          (opt) => opt !== value
        );
      } else {
        newStatuses[header] = [...(newStatuses[header] || []), value];
      }
      return newStatuses;
    };

    setSelectedOptions(optionType, formattedOptions());
  };

  const filterPillOptions = Object.values(selectedOptions).reduce(
    (acc, optionsArray) => acc.concat(optionsArray),
    [] as string[]
  );

  const singleFilterValue = () => {
    return filterPillOptions[0];
  };

  const isChecked = useMemo(
    () => (header: string, option: { id: string }) => {
      return (
        header in selectedOptions &&
        selectedOptions?.[header]?.includes(option.id)
      );
    },
    [selectedOptions]
  );

  // Click outside handling
  useClickOutside([menuRef, triggerRef], handleClose, isOpen);
  useClickOutside(
    [moreOptionsMenuRef, menuRef],
    handleCloseMoreOptions,
    isMoreOptionsOpen
  );

  // Handle scroll for infinite loading
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const container = containerRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight) {
        onMenuScroll(false);
      }
    }
  };

  // Update position on scroll/resize when menu is open
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('scroll', updateMenuPosition, true);
      window.addEventListener('resize', updateMenuPosition);
      return () => {
        window.removeEventListener('scroll', updateMenuPosition, true);
        window.removeEventListener('resize', updateMenuPosition);
      };
    }
  }, [isOpen]);

  const menuId = `${testId}-menu`;
  const buttonId = `${testId}-button`;

  return (
    <>
      <div ref={triggerRef} className="w-full">
        <button
          id={buttonId}
          onClick={handleOpenMenu}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? menuId : undefined}
          aria-label={`${filterHeader}: ${
            allSelected
              ? 'All selected'
              : selectedOptionsCombinedLength > 0
              ? `${selectedOptionsCombinedLength} selected`
              : 'None selected'
          }`}
          data-testid={testId}
          className="inline-flex items-center justify-between w-full min-h-8 px-3 py-1.5 rounded-full border border-neutral-200 bg-white text-sm font-semibold"
        >
          <span className="flex items-center gap-2 flex-1 min-w-0">
            {startDecorator}
            {!allSelected && selectedOptionsCombinedLength > 1 ? (
              <FilterPill
                selectedOptions={filterPillOptions}
                handleClearFilters={deselectAll}
              />
            ) : (
              <span className="flex-1 text-left text-neutral-800 truncate font-normal">
                {!allSelected && selectedOptionsCombinedLength === 1
                  ? singleFilterValue()
                  : 'All'}
              </span>
            )}
          </span>
          <Icon
            icon={faCaretDown}
            className={`w-3.5 h-3.5 text-neutral-500 transition-transform duration-200 ${
              isOpen ? '-rotate-90' : 'rotate-0'
            }`}
          />
        </button>
      </div>

      {isOpen &&
        createPortal(
          <div
            id={menuId}
            ref={menuRef}
            role="listbox"
            aria-labelledby={buttonId}
            data-testid={`${testId}-menu`}
            className="fixed z-50 min-w-60 p-2 flex flex-col items-start rounded-lg border border-neutral-300 bg-white shadow-md"
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
            }}
          >
            <div className="flex flex-col w-full">
              {/* Header with more options */}
              <div className="flex justify-between items-center pl-2 pr-1">
                <span className="text-sm font-medium text-neutral-800">
                  {filterHeader}
                </span>
                <div className="relative">
                  <button
                    onClick={handleMoreOptionsClick}
                    className="p-1.5 rounded hover:bg-neutral-100 text-neutral-600 transition-colors"
                    aria-label="More Options"
                  >
                    <Icon icon={faEllipsisV} className="h-3 w-3" />
                  </button>
                  {isMoreOptionsOpen && (
                    <div
                      ref={moreOptionsMenuRef}
                      className="absolute right-0 top-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg z-10 min-w-[120px]"
                    >
                      <button
                        onClick={selectAll}
                        data-testid="select-all-btn"
                        className="w-full px-3 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-100 transition-colors rounded-t-lg"
                      >
                        Select All
                      </button>
                      <button
                        onClick={deselectAll}
                        data-testid="deselect-all-btn"
                        className="w-full px-3 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-100 transition-colors rounded-b-lg"
                      >
                        Deselect All
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Sections */}
              {exists(optionData) &&
                Object.entries(optionData).map(([header, options], i) => {
                  return (
                    <div
                      key={header}
                      className={`border-t border-neutral-200 ${
                        expandedSections[header] ? 'px-2 pt-2' : 'p-2'
                      } ${i === 0 ? 'mt-2' : ''}`}
                    >
                      <button
                        className="flex justify-between items-center w-full text-left text-xs font-bold text-neutral-800 bg-transparent border-none p-0 cursor-pointer"
                        onClick={() => toggleSection(header)}
                        onKeyDown={(e: React.KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleSection(header);
                          }
                        }}
                        aria-expanded={expandedSections[header]}
                        aria-controls={`${testId}-section-${header}`}
                        data-testid={`${testId}-section-header-${header}`}
                      >
                        {header}
                        <Icon
                          icon={
                            expandedSections[header] ? faAngleUp : faAngleDown
                          }
                          aria-hidden="true"
                          className="text-neutral-500"
                        />
                      </button>
                      {expandedSections[header] && (
                        <div
                          id={`${testId}-section-${header}`}
                          ref={containerRef}
                          onScroll={handleScroll}
                          role="group"
                          aria-label={`${header} options`}
                          data-testid={`${testId}-section-${header}`}
                          className="max-h-[200px] w-full overflow-auto"
                        >
                          {(options as any[])
                            ?.sort((a, b) => {
                              const aChecked = selectedOptions[
                                header
                              ]?.includes(a.id);
                              const bChecked = selectedOptions[
                                header
                              ]?.includes(b.id);
                              return aChecked === bChecked
                                ? 0
                                : aChecked
                                ? -1
                                : 1;
                            })
                            .map((option) => {
                              return (
                                <div
                                  key={option.id}
                                  className="flex items-center gap-3 px-2 py-1.5 w-full h-6"
                                >
                                  <Checkbox
                                    size="sm"
                                    variant="outlined"
                                    color="neutral"
                                    label={option.label}
                                    value={option.id}
                                    onChange={(e) =>
                                      handleCheckboxChange(header, e)
                                    }
                                    checked={isChecked(header, option)}
                                    data-testid={`${testId}-checkbox-${option.id}`}
                                    aria-label={option.label}
                                  />
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  );
                })}
              {isLoading && (
                <div className="flex justify-center py-2">
                  <CircularProgress size="sm" />
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default SegmentedMultiSelectMenu;
